import React from "react";
import {Alert, Button, Form} from "react-bootstrap";
import {CloudDownload} from "react-bootstrap-icons";
import {errorMessage} from "../Alerts/Const";
import {DismissibleAlert} from "../Alerts/DismissibleAlert";
import {USER_EMAIL} from "../Auth/LoginUser";
import {EventType, FrontendModule} from "../CommonApi/Enum";
import {UsageTracking} from "../CommonApi/UsageTracking";
import {ApiCallStatus} from "../CommonFunc/Enum";
import {DataConversionApiClient} from "./DataConversionApiClient";
import {DownloadButton} from "../CommonUIComponent/DownloadButton";
import {DATA_CONVERSION_BUCKET_NAME, dataConversionApiConsts} from "./Const";
import {ConversionResponse, IC50BindingPutApiRequest} from "./ApiModels";
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { ConsoleLogger } from 'aws-amplify/utils';
import "./DataConversion.css";

interface IC50BindingInputFormProps {
}

interface IC50BindingInputFormState {
    ic50DataFile: File | null,
    ic50DataFileError: string,
    rawDataFile: File | null,
    rawDataFileError: string,
    submissionStatus: ApiCallStatus,
    submissionErrorMsg: string,
    conversionId: string | null,
    conversionResponse: ConversionResponse[] | null
}

const logger = new ConsoleLogger("IC50BindingInputForm.tsx")

export class IC50BindingInputForm extends React.Component<IC50BindingInputFormProps, IC50BindingInputFormState> {
    constructor(props: any) {
        super(props);
        this.state = {
            ic50DataFile: null,
            ic50DataFileError: "",
            rawDataFile: null,
            rawDataFileError: "",
            submissionStatus: ApiCallStatus.NoData,
            submissionErrorMsg: "",
            conversionId: null,
            conversionResponse: null
        }
    }

    validateFileName(fileName: string): string {
        const pattern = /^US\d{3}-\d{7}-O/;
        if (!pattern.test(fileName)) {
            return "File name must start with study ID (e.g., US073-0037359-O).";
        }
        return "";
    }

    setIC50DataFile(e: any) {
        const file = e.target.files[0];
        const error = file ? this.validateFileName(file.name) : "";
        this.setState({
            submissionErrorMsg: "",
            ic50DataFile: file,
            ic50DataFileError: error
        })
    }

    setRawDataFile(e: any) {
        const file = e.target.files[0];
        const error = file ? this.validateFileName(file.name) : "";
        this.setState({
            submissionErrorMsg: "",
            rawDataFile: file,
            rawDataFileError: error
        })
    }

    clearForm() {
        // Clear the form inputs
        const ic50DataFileElement = document.getElementById("ic50-binding-ic50-data-upload") as HTMLInputElement;
        const rawDataFileElement = document.getElementById("ic50-binding-raw-data-upload") as HTMLInputElement;
        
        if (ic50DataFileElement) ic50DataFileElement.value = "";
        if (rawDataFileElement) rawDataFileElement.value = "";
        
        // Reset state to initial values
        this.setState({
            ic50DataFile: null,
            ic50DataFileError: "",
            rawDataFile: null,
            rawDataFileError: "",
            submissionStatus: ApiCallStatus.NoData,
            submissionErrorMsg: "",
            conversionId: null,
            conversionResponse: null
        });
    }

    submitPipeline(e: any) {
        e.preventDefault()
        
        if (!this.state.ic50DataFile || !this.state.rawDataFile) {
            this.setState({
                submissionErrorMsg: "Please select both IC50 data file and raw data file",
                submissionStatus: ApiCallStatus.Error
            })
            return
        }

        const ic50Error = this.validateFileName(this.state.ic50DataFile.name);
        const rawError = this.validateFileName(this.state.rawDataFile.name);
        if (ic50Error || rawError) {
            this.setState({
                ic50DataFileError: ic50Error,
                rawDataFileError: rawError,
                submissionErrorMsg: "File names must start with study ID (e.g., US073-0037359-O).",
                submissionStatus: ApiCallStatus.Error
            })
            return
        }

        const reportID: string = uuidv4();

        this.setState({
            submissionStatus: ApiCallStatus.Loading,
            submissionErrorMsg: ""
        }, async () => {
            try {
                // Upload IC50 data file to S3 first
                const ic50DataFileName = this.state.ic50DataFile!.name;
                const ic50DataS3Key = `public/${reportID}/${ic50DataFileName}`;
                
                logger.info("Starting IC50 data file upload...");
                const ic50DataUploadResult = await uploadData({
                    path: ic50DataS3Key,
                    data: this.state.ic50DataFile!,
                    options: {
                        bucket: DATA_CONVERSION_BUCKET_NAME
                    }
                }).result;
                logger.info("IC50 data file upload completed:", ic50DataUploadResult);

                // Upload raw data file to S3
                const rawDataFileName = this.state.rawDataFile!.name;
                const rawDataS3Key = `public/${reportID}/${rawDataFileName}`;
                
                logger.info("Starting raw data file upload...");
                const rawDataUploadResult = await uploadData({
                    path: rawDataS3Key,
                    data: this.state.rawDataFile!,
                    options: {
                        bucket: DATA_CONVERSION_BUCKET_NAME
                    }
                }).result;
                logger.info("Raw data file upload completed:", rawDataUploadResult);

                UsageTracking.recordEvent({
                    uuid: uuidv4(),
                    module: FrontendModule.DataConversion,
                    event: EventType.Import,
                    table: dataConversionApiConsts.TABLE_NAME,
                    database: dataConversionApiConsts.DATABASE_NAME,
                    extraMetadata: {
                        "conversionMethod": "IC50Binding",
                        "owner": USER_EMAIL,
                        "conversionRequestUuid": reportID,
                        "ic50DataFileName": ic50DataFileName,
                        "rawDataFileName": rawDataFileName,
                    }
                })

                // Now call the API with S3 paths after both uploads are complete
                const request: IC50BindingPutApiRequest = {
                    conversionRequestUuid: reportID,
                    ic50DataFileS3Path: ic50DataS3Key,
                    rawDataFileS3Path: rawDataS3Key,
                    userEmail: USER_EMAIL
                };
                
                logger.info("Starting API call with S3 paths:", request);
                const response = await DataConversionApiClient.convertIC50Binding(request);
                logger.info("IC50 Binding conversion response:", response);
                
                this.setState({
                    submissionStatus: ApiCallStatus.Completed,
                    conversionId: response.data[0]?.requestId || reportID,
                    conversionResponse: response.data
                });
            } catch (err: any) {
                logger.error("Error during upload or conversion:", err);
                this.setState({
                    submissionStatus: ApiCallStatus.Error,
                    submissionErrorMsg: errorMessage(err)
                });
            }
        })
    }

    render() {
        return (
            <>
                <h4 className="protocol-title">IC50 Binding Conversion</h4>
                <Alert className="info-subtle">Converts Eurofins IC50 binding data for IC50_binding protocol import into CDD. Requires two input files: <strong>US***_Kd Report.csv</strong> and <strong>US***_RawData Report.csv</strong></Alert>
                
                <div className="row">
                    <div className="col-md-6">
                        <Form onSubmit={this.submitPipeline.bind(this)}>
                            <Form.Group className="mb-3">
                                <Alert className="info-subtle">
                                    <div>
                                        Files must follow the required format - download examples:
                                        <ul>
                                            <li className="mb-2">
                                                <DownloadButton 
                                                    buttonSize={"sm"} 
                                                    s3Bucket={DATA_CONVERSION_BUCKET_NAME} 
                                                    s3Keys={["examples/US073-0037359-O-example_RawData Report.csv"]} 
                                                    variant={"success"} 
                                                    text={"Raw Data Example"}
                                                    icon={<CloudDownload className="me-2" />}
                                                />
                                            </li>
                                            <li>
                                                <DownloadButton 
                                                    buttonSize={"sm"} 
                                                    s3Bucket={DATA_CONVERSION_BUCKET_NAME} 
                                                    s3Keys={["examples/US073-0037359-O-example_Kd Report.csv"]} 
                                                    variant={"success"} 
                                                    text={"Kd Report Example"}
                                                    icon={<CloudDownload className="me-2" />}
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                </Alert>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload IC50 Raw Data file (US***_RawData Report.csv)</Form.Label>
                                <Form.Control
                                    id="ic50-binding-raw-data-upload"
                                    type="file"
                                    accept=".csv"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onChange={this.setRawDataFile.bind(this)}
                                    isInvalid={!!this.state.rawDataFileError}
                                    required
                                />
                                {this.state.rawDataFileError && (
                                    <Form.Control.Feedback type="invalid">
                                        {this.state.rawDataFileError}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Upload IC50 Kd Report file (US***_Kd Report.csv)</Form.Label>
                                <Form.Control
                                    id="ic50-binding-ic50-data-upload"
                                    type="file"
                                    accept=".csv"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onChange={this.setIC50DataFile.bind(this)}
                                    isInvalid={!!this.state.ic50DataFileError}
                                    required
                                />
                                {this.state.ic50DataFileError && (
                                    <Form.Control.Feedback type="invalid">
                                        {this.state.ic50DataFileError}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                >
                                    {this.state.submissionStatus === ApiCallStatus.Loading ? "Converting ..." : "Convert"}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    disabled={this.state.submissionStatus === ApiCallStatus.Loading}
                                    onClick={this.clearForm.bind(this)}
                                >
                                    Clear
                                </Button>
                            </div>
                        </Form>
                        {this.state.submissionStatus === ApiCallStatus.Error && <DismissibleAlert className={"mt-2"} message={this.state.submissionErrorMsg} />}
                    </div>
                    
                    <div className="col-md-6">
                        {this.state.submissionStatus === ApiCallStatus.Completed && (
                            <Alert variant={"success"}>
                                <Alert.Heading>Conversion Completed!</Alert.Heading>
                                
                                {this.state.conversionResponse && this.state.conversionResponse.length > 0 && (
                                    <div className="mb-3">
                                        <DownloadButton 
                                            buttonSize="sm" 
                                            s3Bucket={this.state.conversionResponse[0].outputZipFileS3Bucket || ''} 
                                            s3Keys={[this.state.conversionResponse[0].outputZipFileS3Key || '']} 
                                            variant="success" 
                                            text="Download Converted CSV File"
                                            icon={<CloudDownload className="me-2" />}
                                        />
                                    </div>
                                )}
                            </Alert>
                        )}
                        {this.state.submissionStatus === ApiCallStatus.Loading && (
                            <Alert variant={"info"}>
                                <Alert.Heading>Conversion in progress...</Alert.Heading>
                                <p>Please wait while we convert your files...</p>
                            </Alert>
                        )}
                    </div>
                </div>
            </>
        );
    }
}
